import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from '~/lib/api'

// Keys start with the domain; team-bound screens carry team + season (ARCHITECTURE §6.2), so the
// server's ['medical'] invalidation and team/season switches refetch exactly this data.
export const medicalKeys = {
  all: ['medical'] as const,
  overview: (teamId: string, season: string) => ['medical', teamId, season, 'overview'] as const,
  injury: (id: string) => ['medical', 'injury', id] as const,
  person: (personId: string) => ['medical', 'person', personId] as const,
}

export const overviewQuery = (teamId: string, season: string) =>
  queryOptions({
    queryKey: medicalKeys.overview(teamId, season),
    queryFn: () => unwrap(api.teams[':teamId'].medical.$get({ param: { teamId }, query: { season } })),
    enabled: !!teamId,
  })

export type Overview = Awaited<ReturnType<NonNullable<ReturnType<typeof overviewQuery>['queryFn']>>>
export type OutEntry = Overview['out'][number]
export type HistoryInjury = Overview['injuries'][number]
export type CalendarAbsence = Overview['calendar']['items'][number]
export type SquadPerson = Overview['squad'][number]
export type CardEntry = Overview['cards'][number]

export const injuryQuery = (id: string) =>
  queryOptions({
    queryKey: medicalKeys.injury(id),
    queryFn: () => unwrap(api.injuries[':id'].$get({ param: { id } })),
  })
export type InjuryDetail = Awaited<ReturnType<NonNullable<ReturnType<typeof injuryQuery>['queryFn']>>>

export const personHealthQuery = (personId: string) =>
  queryOptions({
    queryKey: medicalKeys.person(personId),
    queryFn: () => unwrap(api.people[':personId'].health.$get({ param: { personId } })),
  })
export type PersonHealthData = Awaited<ReturnType<NonNullable<ReturnType<typeof personHealthQuery>['queryFn']>>>



/** Availability changes ripple through squad, matches, training and home. */
function useRefresh() {
  const qc = useQueryClient()
  return () =>
    Promise.all(
      [['medical'], ['people'], ['matches'], ['training'], ['home']].map((queryKey) => qc.invalidateQueries({ queryKey })),
    )
}

type InjuryCreate = Parameters<typeof api.injuries.$post>[0]['json']
type InjuryPatch = Parameters<(typeof api.injuries)[':id']['$patch']>[0]['json']
type AbsenceCreate = Parameters<typeof api.absences.$post>[0]['json']
type AbsencePatch = Parameters<(typeof api.absences)[':id']['$patch']>[0]['json']
export type InjuryStatusValue = Parameters<(typeof api.injuries)[':id']['status']['$post']>[0]['json']['status']

export function useReportInjury() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (json: InjuryCreate) => unwrap(api.injuries.$post({ json })),
    onSuccess: refresh,
  })
}

export function useUpdateInjury(id: string) {
  const qc = useQueryClient()
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (json: InjuryPatch) => unwrap(api.injuries[':id'].$patch({ param: { id }, json })),
    onSuccess: (data) => {
      qc.setQueryData(medicalKeys.injury(id), data)
      return refresh()
    },
  })
}

export function useInjuryStatus(id: string) {
  const qc = useQueryClient()
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (json: { status: InjuryStatusValue; on?: string }) =>
      unwrap(api.injuries[':id'].status.$post({ param: { id }, json })),
    onSuccess: (data) => {
      qc.setQueryData(medicalKeys.injury(id), data)
      return refresh()
    },
  })
}

export function useDeleteInjury() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (id: string) => unwrap(api.injuries[':id'].$delete({ param: { id } })),
    onSuccess: refresh,
  })
}

export function useSaveAbsence() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (v: { id?: string; json: AbsenceCreate | AbsencePatch }) =>
      v.id
        ? unwrap(api.absences[':id'].$patch({ param: { id: v.id }, json: v.json as AbsencePatch }))
        : unwrap(api.absences.$post({ json: v.json as AbsenceCreate })),
    onSuccess: refresh,
  })
}

export function useDeleteAbsence() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (id: string) => unwrap(api.absences[':id'].$delete({ param: { id } })),
    onSuccess: refresh,
  })
}


export { loaderScope } from '~/lib/session'
